import LocalWallpaper from "@/classes/local-wallpaper";
import {
	changeEditorFontSize,
	editorInstance,
	enableMonacoEditor,
} from "@/code-editor";
import PopupShell from "@/components/popup-shell";
import SettingsItem from "@/components/settings-item";
import { selectLocalFile } from "@/file";
import globals from "@/globals";
import {
	changeTheme,
	resetTheme,
	saveSettings,
	uploadSettings,
} from "@/settings";
import { SettingsItemInfo } from "@/types";
import { closePopup, isPremium, showDialog } from "@/utils";
import { DialogTitle } from "@headlessui/react";
import i18next, { t } from "i18next";
import { ChangeEvent, JSX, useCallback, useMemo, useState } from "react";

function SettingsPopup(): JSX.Element {
	const [isClosing, setIsClosing] = useState(false);

	const closeSettingsPopup = useCallback((): void => {
		setIsClosing(true);
		window.setTimeout(() => {
			closePopup("settings");
			setIsClosing(false);
		}, globals.ANIMATION_WAIT_TIME);
	}, []);

	const settingsItems = useMemo(
		(): SettingsItemInfo[] => [
			{
				default: i18next.language,
				key: "language",
				label: t("language"),
				options: [
					{
						text: "简体中文",
						value: "zh-CN",
					},
					{
						text: "繁體中文",
						value: "zh-TW",
					},
					{
						text: "English",
						value: "en-US",
					},
				],
				reloadRequired: (newValue: string): boolean => {
					return newValue !== i18next.language;
				},
			},
			{
				default: globals.settings.theme || "dark",
				key: "theme",
				label: t("theme"),
				onChange: async (
					newValue: string,
					oldValue: string | undefined,
					target: HTMLInputElement | HTMLSelectElement | undefined,
				): Promise<void> => {
					const localWallpaper = LocalWallpaper.getInstance();
					if (newValue === "wallpaper") {
						if (isPremium()) {
							await showDialog(t("selectImage"));
							selectLocalFile(
								{
									accept: "image/*",
									multiple: false,
								},
								(files) => {
									void localWallpaper.saveWallpaper(files[0]);
								},
							);
						} else if (
							oldValue &&
							target instanceof HTMLSelectElement
						) {
							resetTheme(oldValue, target);
						}
					} else {
						void localWallpaper.deleteWallpaper();
						document.documentElement.classList.remove("has-bg-img");
					}
					changeTheme(
						newValue === "light" ||
							(newValue === "system" &&
								matchMedia("(prefers-color-scheme: light)")
									.matches),
					);
				},
				options: [
					{
						text: t("systemDefault"),
						value: "system",
					},
					{
						text: t("light"),
						value: "light",
					},
					{
						text: t("dark"),
						value: "dark",
					},
					{
						text: t("customWallpaper"),
						value: "wallpaper",
					},
				],
			},
			{
				confirm: {
					text: t("thisCodeEditorNotOptimizedForMobile"),
					when: (newValue: string): boolean => {
						return (
							globals.isMobile &&
							!editorInstance &&
							newValue === "vscode"
						);
					},
				},
				default: editorInstance ? "vscode" : "native",
				key: "codeEditor",
				label: t("codeEditor"),
				onChange: (newValue: string): void => {
					if (!editorInstance && newValue === "vscode") {
						void enableMonacoEditor();
					}
				},
				options: [
					{
						text: t("nativeTextBox"),
						value: "native",
					},
					{
						text: "Monaco Editor (VSCode)",
						value: "vscode",
						when: globals.isMonacoSupported,
					},
				],
				reloadRequired: (newValue: string): boolean => {
					return !!editorInstance && newValue === "native";
				},
			},
			{
				default: globals.settings.fontSize,
				key: "fontSize",
				label: t("fontSize"),
				onChange: (newValue: string): void => {
					changeEditorFontSize(false); // no need to save twice
					void uploadSettings("fontSize", newValue);
				},
				options: Array.from({ length: 31 }, (_, i) =>
					(i * 2 + 12).toString(),
				),
			},
			{
				confirm: {
					text: t("confirmEnableJsProcessor"),
					when: (newValue: string): boolean => {
						return !!newValue;
					},
				},
				default: globals.settings.jsProcessor || "none",
				key: "jsProcessor",
				label: t("jsProcessor"),
				onChange: (newValue: string): void => {
					void uploadSettings("jsProcessor", newValue || "false");
				},
				options: [
					{
						text: t("none"),
						value: "none",
					},
					{
						text: "JavaScript Obfuscator",
						value: "obfuscator",
					},
				],
			},
		],
		[],
	);

	const settingsItemElements = settingsItems.map((item) => {
		const handleChange = async (
			event: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
		): Promise<void> => {
			const { target } = event;
			const newValue: string =
				target.value === "none" ? "" : target.value;

			if (item.confirm && item.confirm.when?.(newValue)) {
				await showDialog(
					item.confirm.text,
					{
						showCancel: true,
					},
					{
						cancel: () => {
							target.value = item.default.toString();
						},
					},
				);
			}

			const oldValue = globals.settings[item.key] || item.default;
			globals.settings[item.key] = newValue;
			saveSettings();
			void item.onChange?.(newValue, oldValue.toString(), target);

			if (item.reloadRequired && item.reloadRequired(newValue)) {
				await showDialog(t("refreshToApplySettings"), {
					showCancel: true,
				});
				window.location.reload();
			}
		};

		return (
			<SettingsItem
				key={item.key}
				item={item}
				onChange={(event) => void handleChange(event)}
			/>
		);
	});

	return (
		<PopupShell
			isClosing={isClosing}
			onClose={closeSettingsPopup}
			isOpen
		>
			<DialogTitle as="h1">{t("settings")}</DialogTitle>
			{settingsItemElements}
			<div className="button-container">
				<button
					className="default-button"
					type="button"
					onClick={closeSettingsPopup}
				>
					{t("ok")}
				</button>
			</div>
		</PopupShell>
	);
}

export default SettingsPopup;
